/* Real Recipe V2 API client (Slice 1: basic CRUD only, backed by IM's recipev2/recipes endpoints). */

import { normalizeRecipeAllergenStatus, recipeAllergenRiskPriority } from './allergenStatus';
import { IRecipeV2Client } from './IRecipeV2Client';
import { RecipeV2ClientMocked } from './recipeV2ClientMocked';
import {
  RecipeV2,
  RecipeV2Detail,
  RecipeAudienceDetail,
  RecipeV2ListResponse,
  RecipeMenuUsage,
  RecipeComponent,
  ProductAllergen,
  AllergenTraceEntry,
  CreateRecipeRequest,
  CloneRecipeRequest,
  ApproveRejectRequest,
  AllergenOverride,
  CostSimulationResult,
  ComponentSupplierComparison,
  StockItemSearchResult,
  ProductionMethod,
  Allergen,
  RecipeStep,
  RecipeMediaUploadAuthorization,
  CreateUpdateStepRequest,
  UpdateComponentRequest,
  UpdateRecipeMetadataRequest,
  DietaryAttribute,
  DietaryAttributeOverride,
  DietaryAttributeReference,
  NutritionOverride,
  NutrientReference,
  RecipeBulkImportRun,
  RecipeStatus,
  ConvertToStockRequest,
  ConvertToStockResult,
  AllergenStatus,
  AllergenSource,
  DietaryCategory,
  DietaryAttributeStatus,
  RecipeVersionHistory,
  RecipeVersionDetail,
  RecipeVersionComponentSnapshot,
  RecipeVersionPublication,
  RecipeExportInformationProfile,
  RecipeExportPresentationStyle,
} from './recipeV2Types';

type ApiEnvelope<T> = { data?: T };

interface RecipeYieldApiDto {
  id: number;
  quantity: number;
  uom: string;
  displayOrder: number;
}

interface RecipeComponentApiDto {
  componentId: number;
  componentKey?: string;
  componentItemId: number;
  componentRole: 'Ingredient' | 'Packaging' | 'Operational' | 'NestedRecipe';
  sortOrder: number;
  componentItemNumber: string;
  componentDescription: string;
  quantity: number;
  uom: string;
  weight: number | null;
  weightSource?: 'Authored' | 'Derived' | 'Legacy' | 'Unknown';
  weightNet: number;
  volume: number | null;
  volumeSource?: 'Authored' | 'Derived' | 'Legacy' | 'Unknown';
  componentCost: number | null;
  costIsComplete: boolean;
  missingCostComponentItemNumbers: string[];
  isRecipe: boolean;
  children: RecipeComponentApiDto[];
  allergens: { allergenId: number; code: string; name: string; status: string; source?: string | null }[];
  dietaryAttributes?: DietaryAttributeApiDto[];
  nutrition: { code: string; name: string; value: number | null; valueUom?: string | null; basisType?: string | null; basisQuantity?: number | null; basisUom?: string | null; servingSize?: number | null; servingUom?: string | null }[];
}


type RecipePublishingComponentApiDto = Omit<
  RecipeComponentApiDto,
  'componentCost' | 'costIsComplete' | 'missingCostComponentItemNumbers' | 'children' | 'allergens' | 'dietaryAttributes' | 'nutrition'
> & {
  children: RecipePublishingComponentApiDto[];
};


interface RecipeApiDto {
  id: number;
  franchiseId: number;
  itemNumber: string;
  stockUom?: string | null;
  description: string;
  narrativeDescription?: string | null;
  primaryCategory: string;
  status: string;
  cost: number | null;
  costPerPortion: number | null;
  costIsComplete: boolean;
  missingCostComponentItemNumbers: string[];
  mediaUrl?: string | null;
  recipeVersionId?: number | null;
  recipePublicationId?: number | null;
  complianceRevisionIds?: number[];
  effectiveFrom: string | null;
  effectiveTo: string | null;
  productionMethodId: number | null;
  currentVersionNumber: number | null;
  yieldPortions: number | null;
  portionQuantity: number | null;
  portionUom: string | null;
  expectedYieldLossPercent: number | null;
  consumptionMode: 'PerPortion' | 'WholeYield' | 'ProducedStock';
  productionTrigger: 'Automatic' | 'Manual' | null;
  storageMethod: string | null;
  shelfLifeValue: number | null;
  shelfLifeUnit: string | null;
  componentCount?: number;
  yields: RecipeYieldApiDto[];
  components: RecipeComponentApiDto[];
  allergens?: { allergenId: number; code: string; name: string; status: string; source?: string | null }[];
  dietaryAttributes?: DietaryAttributeApiDto[];
  nutrition?: import('./recipeV2Types').RecipeNutrition[];
  steps?: RecipeStepApiDto[];
}


interface RecipeAudienceApiDto {
  id: number;
  franchiseId: number;
  itemNumber: string;
  description: string;
  narrativeDescription?: string | null;
  primaryCategory: string;
  mediaUrl?: string | null;
  recipeVersionId?: number | null;
  recipePublicationId?: number | null;
  complianceRevisionIds?: number[];
  currentVersionNumber?: number | null;
  allergens?: { allergenId: number; code: string; name: string; status: string; source?: string | null }[];
  dietaryAttributes?: DietaryAttributeApiDto[];
  nutrition?: import('./recipeV2Types').RecipeNutrition[];
}

type RecipePublishingApiDto = Omit<
  RecipeApiDto,
  'cost' | 'costPerPortion' | 'costIsComplete' | 'missingCostComponentItemNumbers' | 'components'
> & {
  components: RecipePublishingComponentApiDto[];
};


interface PagedRecipeApiDto {
  data: RecipeApiDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

interface ItemSearchApiDto {
  id: number;
  itemNumber: string;
  description: string;
  uom: string;
  costPrice: number;
  weightNet: number;
  isBom: boolean;
  isStockItem: boolean;
}

interface RecipeStepApiDto {
  id: number;
  recipeId: number;
  stepNumber: number;
  title: string;
  instruction: string;
  productionMethodId: number | null;
  durationMinutes: number | null;
  temperature: string | null;
  equipment: { id: number; name: string }[];
}

interface RecipeVersionHistoryApiDto {
  id: number;
  recipeId: number;
  versionNumber: number;
  status: string;
  versionOrigin: string;
  basedOnRecipeVersionId: number | null;
  changeReason: string | null;
  createdBy: number | null;
  createdDate: string;
  modifiedBy: number | null;
  modifiedDate: string | null;
  latestDecision: string | null;
  decisionBy: number | null;
  decisionDate: string | null;
  decisionReason: string | null;
}

interface RecipeVersionPublicationApiDto {
  id: number;
  recipeId: number;
  recipeVersionId: number;
  scopeType: string;
  scopeId: number | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  status: string;
  createdBy: number | null;
  createdDate: string;
  publishedBy: number | null;
  publishedDate: string | null;
  withdrawnBy: number | null;
  withdrawnDate: string | null;
}

interface RecipeVersionComponentSnapshotApiDto {
  id: number;
  componentKey: string;
  componentItemId: number;
  uom: string;
  componentRole: 'Ingredient' | 'Packaging' | 'Operational' | 'NestedRecipe';
  nestedRecipeVersionId: number | null;
  quantity: number;
  weight: number | null;
  weightSource: 'Authored' | 'Derived' | 'Legacy' | 'Unknown';
  volume: number | null;
  volumeSource: 'Authored' | 'Derived' | 'Legacy' | 'Unknown';
  sortOrder: number | null;
}

interface RecipeVersionDetailApiDto {
  version: RecipeVersionHistoryApiDto;
  productionMethodId: number | null;
  yieldPortions: number | null;
  portionQuantity: number | null;
  portionUom: string | null;
  expectedYieldLossPercent: number | null;
  consumptionMode: 'PerPortion' | 'WholeYield' | 'ProducedStock';
  productionTrigger: 'Automatic' | 'Manual' | null;
  storageMethod: string | null;
  shelfLifeValue: number | null;
  shelfLifeUnit: string | null;
  description: string | null;
  primaryCategory: string | null;
  narrativeDescription: string | null;
  heroMediaReference: string | null;
  publication: RecipeVersionPublicationApiDto | null;
  components: RecipeVersionComponentSnapshotApiDto[];
}

const mapStep = (step: RecipeStepApiDto): RecipeStep => ({
  id: step.id, recipeId: step.recipeId, stepNumber: step.stepNumber, title: step.title,
  instruction: step.instruction, productionMethodId: step.productionMethodId,
  durationMinutes: step.durationMinutes, temperature: step.temperature, equipment: step.equipment ?? [],
});

const mapVersionHistory = (version: RecipeVersionHistoryApiDto): RecipeVersionHistory => ({
  id: version.id,
  recipeId: version.recipeId,
  versionNumber: version.versionNumber,
  status: version.status,
  versionOrigin: version.versionOrigin,
  basedOnRecipeVersionId: version.basedOnRecipeVersionId,
  changeReason: version.changeReason,
  createdBy: version.createdBy,
  createdDate: version.createdDate,
  modifiedBy: version.modifiedBy,
  modifiedDate: version.modifiedDate,
  latestDecision: version.latestDecision,
  decisionBy: version.decisionBy,
  decisionDate: version.decisionDate,
  decisionReason: version.decisionReason,
});

const mapVersionPublication = (publication: RecipeVersionPublicationApiDto): RecipeVersionPublication => ({
  id: publication.id,
  recipeId: publication.recipeId,
  recipeVersionId: publication.recipeVersionId,
  scopeType: publication.scopeType,
  scopeId: publication.scopeId,
  effectiveFrom: publication.effectiveFrom,
  effectiveTo: publication.effectiveTo,
  status: publication.status,
  createdBy: publication.createdBy,
  createdDate: publication.createdDate,
  publishedBy: publication.publishedBy,
  publishedDate: publication.publishedDate,
  withdrawnBy: publication.withdrawnBy,
  withdrawnDate: publication.withdrawnDate,
});

const mapVersionComponentSnapshot = (
  component: RecipeVersionComponentSnapshotApiDto,
): RecipeVersionComponentSnapshot => ({
  id: component.id,
  componentKey: component.componentKey,
  componentItemId: component.componentItemId,
  componentRole: component.componentRole,
  nestedRecipeVersionId: component.nestedRecipeVersionId,
  quantity: component.quantity,
  uom: component.uom,
  weight: component.weight,
  weightSource: component.weightSource,
  volume: component.volume,
  volumeSource: component.volumeSource,
  sortOrder: component.sortOrder,
});

const mapVersionDetail = (detail: RecipeVersionDetailApiDto): RecipeVersionDetail => ({
  version: mapVersionHistory(detail.version),
  productionMethodId: detail.productionMethodId,
  yieldPortions: detail.yieldPortions,
  portionQuantity: detail.portionQuantity,
  portionUom: detail.portionUom,
  expectedYieldLossPercent: detail.expectedYieldLossPercent,
  consumptionMode: detail.consumptionMode,
  productionTrigger: detail.productionTrigger,
  storageMethod: detail.storageMethod,
  shelfLifeValue: detail.shelfLifeValue,
  shelfLifeUnit: detail.shelfLifeUnit,
  description: detail.description,
  primaryCategory: detail.primaryCategory,
  narrativeDescription: detail.narrativeDescription,
  heroMediaReference: detail.heroMediaReference,
  publication: detail.publication ? mapVersionPublication(detail.publication) : null,
  components: (detail.components ?? []).map(mapVersionComponentSnapshot),
});

// IM stores some established allergen values as enum-style strings
// (for example MAY_CONTAIN). Convert at the boundary before deduplication.
const toDietaryCategory = (value: string | null | undefined): DietaryCategory => {
  const normalized = (value ?? '').replace(/[\s_-]/g, '').toLowerCase();
  if (normalized === 'allergen') return DietaryCategory.Allergen;
  if (normalized === 'intolerance') return DietaryCategory.Intolerance;
  if (normalized === 'lifestylepreference') return DietaryCategory.LifestylePreference;
  if (normalized === 'nutritionalclaim') return DietaryCategory.NutritionalClaim;
  return DietaryCategory.DietaryRequirement;
};

const toDietaryStatus = (value: string | null | undefined): DietaryAttributeStatus => {
  const normalized = (value ?? '').replace(/[\s_-]/g, '').toLowerCase();
  if (normalized === 'maycontain') return DietaryAttributeStatus.MayContain;
  if (normalized === 'freefrom') return DietaryAttributeStatus.FreeFrom;
  if (normalized === 'suitable') return DietaryAttributeStatus.Suitable;
  if (normalized === 'notsuitable') return DietaryAttributeStatus.NotSuitable;
  if (normalized === 'certified' || normalized === 'confirmed') return DietaryAttributeStatus.Certified;
  if (normalized === 'contains') return DietaryAttributeStatus.Contains;
  return DietaryAttributeStatus.Unknown;
};

// The dietary-attribute reference endpoint does not return a category (the underlying
// items.DietaryAttributes table has no Category column). Categorise by the curated,
// business-approved name lists supported by the "Add dietary attribute" flow so that
// unsupported/unexpected reference entries (e.g. legacy "Sourcing Sustainability" rows)
// are excluded rather than surfaced under a fallback category.
interface DietaryAttributeApiDto {
  dietaryAttributeId: number;
  code: string;
  name: string;
  category?: string | null;
  status?: string | null;
  source?: string | null;
  originalStatus?: string | null;
  originalSource?: string | null;
  overrideStatus?: string | null;
  overrideSource?: string | null;
  isOverridden?: boolean;
}

const mapDietaryAttribute = (attribute: DietaryAttributeApiDto): DietaryAttribute => ({
  id: attribute.dietaryAttributeId,
  code: attribute.code,
  name: attribute.name,
  category: toDietaryCategory(attribute.category),
  status: toDietaryStatus(attribute.status),
  source: (attribute.source ?? 'Feed') as AllergenSource,
  originalStatus: attribute.originalStatus ? toDietaryStatus(attribute.originalStatus) : undefined,
  originalSource: attribute.originalSource ? (attribute.originalSource as AllergenSource) : undefined,
  overrideStatus: attribute.overrideStatus ? toDietaryStatus(attribute.overrideStatus) : undefined,
  overrideSource: attribute.overrideSource ? (attribute.overrideSource as AllergenSource) : undefined,
  isOverridden: attribute.isOverridden ?? false,
});

const mapComponent = (recipeId: number, c: RecipeComponentApiDto): RecipeComponent => ({
  id: c.componentId,
  componentKey: c.componentKey,
  recipeId,
  componentProductId: c.componentItemId,
  componentRole: c.componentRole,
  componentItemNumber: c.componentItemNumber,
  componentDescription: c.componentDescription,
  quantity: c.quantity,
  weight: c.weight,
  weightSource: c.weightSource ?? 'Unknown',
  volume: c.volume ?? 0,
  volumeSource: c.volumeSource ?? 'Unknown',
  sortOrder: c.sortOrder,
  uom: c.uom,
  costPrice: c.componentCost != null && c.costIsComplete && c.quantity > 0 ? c.componentCost / c.quantity : null,
  costContribution: null,
  costIsComplete: c.costIsComplete,
  missingCostComponentItemNumbers: c.missingCostComponentItemNumbers ?? [],
  weightNet: c.weightNet,
  isRecipe: c.isRecipe,
  children: (c.children ?? []).map((child) => mapComponent(recipeId, child)),
  allergens: (c.allergens ?? []).map((allergen) => ({ productId: c.componentId, allergenId: allergen.allergenId, allergenCode: allergen.code, allergenName: allergen.name, status: normalizeRecipeAllergenStatus(allergen.status), source: allergen.source as import('./recipeV2Types').AllergenSource, updatedBy: '', updatedAt: '' })),
  dietaryAttributes: (c.dietaryAttributes ?? []).map(mapDietaryAttribute),
  nutrition: c.nutrition ?? [],
});

type TracedAllergen = ProductAllergen & { trace?: AllergenTraceEntry };

const collectAllergens = (components: RecipeComponent[], path: string[] = [], depth = 0): TracedAllergen[] => components.flatMap(component => {
  // Introduced By remains the actual component carrying the allergen. Path shows
  // every enclosing sub-recipe, so nested provenance is not flattened away.
  const pathLabel = component.isRecipe
    ? `${component.componentDescription} (sub-recipe)`
    : component.componentDescription;
  const nextPath = [...path, pathLabel];
  const trace = {
    componentId: component.id,
    componentDescription: component.componentDescription,
    depth,
    path: nextPath.join(' > '),
  };
  return [
    ...(component.allergens ?? []).map(allergen => ({ ...allergen, trace })),
    ...collectAllergens(component.children ?? [], nextPath, depth + 1),
  ];
});

const summarizeAllergens = (components: RecipeComponent[], manual: RecipeComponent['allergens'] = []) => {
  const all: TracedAllergen[] = [...(manual ?? []), ...collectAllergens(components)];
  const priority = recipeAllergenRiskPriority;
  const summary = all.reduce<Record<string, { allergen: TracedAllergen; traces: AllergenTraceEntry[] }>>((result, allergen) => {
    const key = String(allergen.allergenId || allergen.allergenCode);
    const current = result[key];
    if (!current || priority(allergen.status) > priority(current.allergen.status)) {
      result[key] = {
        allergen,
        traces: allergen.trace && !current?.traces.some(trace => trace.path === allergen.trace?.path)
          ? [...(current?.traces ?? []), allergen.trace]
          : current?.traces ?? [],
      };
    } else if (allergen.trace && !current.traces.some(trace => trace.path === allergen.trace?.path)) {
      current.traces.push(allergen.trace);
    }
    return result;
  }, {});
  return Object.values(summary)
    .sort((left, right) => left.allergen.allergenName.localeCompare(right.allergen.allergenName) || left.allergen.allergenCode.localeCompare(right.allergen.allergenCode))
    .map(({ allergen, traces }) => ({ allergenId: allergen.allergenId, allergenCode: allergen.allergenCode, allergenName: allergen.allergenName, status: allergen.status, source: allergen.source, introducedBy: traces }));
};

export const mapPublishingComponent = (
  recipeId: number,
  component: RecipePublishingComponentApiDto,
): RecipeComponent => ({
  id: component.componentId,
  recipeId,
  componentProductId: component.componentItemId,
  componentRole: component.componentRole,
  componentItemNumber: component.componentItemNumber,
  componentDescription: component.componentDescription,
  quantity: component.quantity,
  weight: component.weight,
  weightSource: component.weightSource ?? 'Unknown',
  volume: component.volume ?? 0,
  volumeSource: component.volumeSource ?? 'Unknown',
  sortOrder: component.sortOrder,
  uom: component.uom,
  costPrice: null,
  costContribution: null,
  costIsComplete: false,
  missingCostComponentItemNumbers: [],
  weightNet: component.weightNet,
  isRecipe: component.isRecipe,
  children: (component.children ?? []).map((child) => mapPublishingComponent(recipeId, child)),
  allergens: [],
  dietaryAttributes: [],
  nutrition: [],
});

const mapPublishingRecipe = (dto: RecipePublishingApiDto): RecipeV2 => {
  const components = (dto.components ?? []).map((component) => mapPublishingComponent(dto.id, component));
  const manualAllergens = (dto.allergens ?? []).map((allergen) => ({
    productId: dto.id,
    allergenId: allergen.allergenId,
    allergenCode: allergen.code,
    allergenName: allergen.name,
    status: normalizeRecipeAllergenStatus(allergen.status),
    source: allergen.source as import('./recipeV2Types').AllergenSource,
    updatedBy: '',
    updatedAt: '',
  }));
  return {
    id: dto.id,
    franchiseId: dto.franchiseId,
    itemNumber: dto.itemNumber,
    stockUom: dto.stockUom ?? undefined,
    description: dto.description,
    narrativeDescription: dto.narrativeDescription,
    primaryCategory: dto.primaryCategory,
    status: dto.status as RecipeStatus,
    yields: dto.yields,
    costIsComplete: false,
    missingCostComponentItemNumbers: [],
    mediaUrl: dto.mediaUrl ?? undefined,
    recipeVersionId: dto.recipeVersionId ?? undefined,
    recipePublicationId: dto.recipePublicationId ?? undefined,
    complianceRevisionIds: dto.complianceRevisionIds ?? [],
    effectiveFrom: dto.effectiveFrom ?? undefined,
    effectiveTo: dto.effectiveTo ?? undefined,
    productionMethodId: dto.productionMethodId,
    yieldPortions: dto.yieldPortions,
    portionQuantity: dto.portionQuantity,
    portionUom: dto.portionUom,
    expectedYieldLossPercent: dto.expectedYieldLossPercent,
    consumptionMode: dto.consumptionMode,
    productionTrigger: dto.productionTrigger,
    storageMethod: dto.storageMethod,
    shelfLifeValue: dto.shelfLifeValue,
    shelfLifeUnit: dto.shelfLifeUnit,
    currentVersion: dto.currentVersionNumber ?? undefined,
    componentCount: dto.componentCount ?? components.length,
    components,
    allergenSummary: manualAllergens.map((allergen) => ({
      allergenId: allergen.allergenId,
      allergenCode: allergen.allergenCode,
      allergenName: allergen.allergenName,
      status: allergen.status,
      source: allergen.source,
      introducedBy: [],
    })),
    dietaryAttributes: (dto.dietaryAttributes ?? []).map(mapDietaryAttribute),
    nutrition: dto.nutrition ?? [],
  };
};

const mapAudienceRecipe = (dto: RecipeAudienceApiDto): RecipeAudienceDetail => ({
  id: dto.id,
  franchiseId: dto.franchiseId,
  itemNumber: dto.itemNumber,
  description: dto.description,
  narrativeDescription: dto.narrativeDescription,
  primaryCategory: dto.primaryCategory,
  mediaUrl: dto.mediaUrl,
  recipeVersionId: dto.recipeVersionId,
  recipePublicationId: dto.recipePublicationId,
  complianceRevisionIds: dto.complianceRevisionIds ?? [],
  currentVersion: dto.currentVersionNumber ?? undefined,
  allergenSummary: (dto.allergens ?? []).map((allergen) => ({
    allergenId: allergen.allergenId,
    allergenCode: allergen.code,
    allergenName: allergen.name,
    status: normalizeRecipeAllergenStatus(allergen.status),
    source: (allergen.source ?? 'Feed') as AllergenSource,
    introducedBy: [],
  })),
  dietaryAttributes: (dto.dietaryAttributes ?? []).map(mapDietaryAttribute),
  nutrition: dto.nutrition ?? [],
});

export const mapRecipe = (dto: RecipeApiDto, preserveServerCompliance = false): RecipeV2 => {
  const components = (dto.components ?? []).map((c) => mapComponent(dto.id, c));
  // Cost completeness is authoritative from the backend. Never reconstruct a recipe
  // total from only the priced rows because that turns an incomplete cost into an
  // apparently valid commercial total.
  const totalCost = dto.costIsComplete && dto.cost != null ? dto.cost : undefined;
  const costPerPortion = dto.costIsComplete && dto.costPerPortion != null
    ? dto.costPerPortion
    : undefined;
  const manualAllergens = (dto.allergens ?? []).map((allergen) => ({ productId: dto.id, allergenId: allergen.allergenId, allergenCode: allergen.code, allergenName: allergen.name, status: normalizeRecipeAllergenStatus(allergen.status), source: allergen.source as import('./recipeV2Types').AllergenSource, updatedBy: '', updatedAt: '' }));
  return ({
    id: dto.id,
    franchiseId: dto.franchiseId,
    itemNumber: dto.itemNumber,
    stockUom: dto.stockUom ?? undefined,
    description: dto.description,
    narrativeDescription: dto.narrativeDescription,
    primaryCategory: dto.primaryCategory,
    status: dto.status as RecipeStatus,
    yields: dto.yields,
    totalCost,
    costPerPortion,
    costIsComplete: dto.costIsComplete,
    missingCostComponentItemNumbers: dto.missingCostComponentItemNumbers ?? [],
    mediaUrl: dto.mediaUrl ?? undefined,
    recipeVersionId: dto.recipeVersionId ?? undefined,
    recipePublicationId: dto.recipePublicationId ?? undefined,
    complianceRevisionIds: dto.complianceRevisionIds ?? [],
    effectiveFrom: dto.effectiveFrom ?? undefined,
    effectiveTo: dto.effectiveTo ?? undefined,
    productionMethodId: dto.productionMethodId,
    yieldPortions: dto.yieldPortions,
    portionQuantity: dto.portionQuantity,
    portionUom: dto.portionUom,
    expectedYieldLossPercent: dto.expectedYieldLossPercent,
    consumptionMode: dto.consumptionMode,
    productionTrigger: dto.productionTrigger,
    storageMethod: dto.storageMethod,
    shelfLifeValue: dto.shelfLifeValue,
    shelfLifeUnit: dto.shelfLifeUnit,
    currentVersion: dto.currentVersionNumber ?? undefined,
    componentCount: dto.componentCount ?? components.length,
    components,
    allergenSummary: preserveServerCompliance
      ? manualAllergens.map((allergen) => ({
        allergenId: allergen.allergenId,
        allergenCode: allergen.allergenCode,
        allergenName: allergen.allergenName,
        status: allergen.status,
        source: allergen.source,
        introducedBy: [],
      }))
      : summarizeAllergens(components, manualAllergens),
    dietaryAttributes: (dto.dietaryAttributes ?? []).map(mapDietaryAttribute),
    nutrition: dto.nutrition ?? [],
  });
};

/**
 * Backs Recipe V2 persisted operations with the real IM API. Remaining unfinished methods
 * still delegate to the mock client until they get their own backend slice.
 */
export class RecipeV2ClientApi implements IRecipeV2Client {
  private readonly baseUrl = '/api/recipev2/recipes';
  private readonly fallback = new RecipeV2ClientMocked();

  private async getHeaders(): Promise<HeadersInit> {
    const token = await window.Clerk?.session?.getToken?.({ template: 'Orderly_default' });
    const storedStore = localStorage.getItem('selectedStore');
    const store = storedStore ? JSON.parse(storedStore) : null;

    return {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(store?.storeNumber && { 'X-Context-Store-Number': store.storeNumber }),
      ...(store?.franchiseId && { 'X-Context-Franchise-Id': String(store.franchiseId) }),
    };
  }

  private async getMultipartHeaders(): Promise<HeadersInit> {
    const token = await window.Clerk?.session?.getToken?.({ template: 'Orderly_default' });
    const storedStore = localStorage.getItem('selectedStore');
    const store = storedStore ? JSON.parse(storedStore) : null;

    return {
      Accept: 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(store?.storeNumber && { 'X-Context-Store-Number': store.storeNumber }),
      ...(store?.franchiseId && { 'X-Context-Franchise-Id': String(store.franchiseId) }),
    };
  }

  private async extractErrorMessage(response: Response, fallback: string): Promise<string> {
    try {
      const errorData = await response.clone().json() as { message?: string; title?: string; errors?: Record<string, string[]> } | string;
      // Some endpoints return BadRequest(ex.Message), which serializes as a raw JSON string.
      if (typeof errorData === 'string') {
        return errorData || fallback;
      }
      const validationErrors = errorData.errors
        ? Object.values(errorData.errors).flat().filter(Boolean).join(' ')
        : '';
      return errorData.message || validationErrors || errorData.title || fallback;
    } catch {
      const text = await response.text();
      return text || fallback;
    }
  }

  private async readResponse<T>(response: Response, fallbackMessage: string): Promise<T> {
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, fallbackMessage));
    }

    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined as T;
    }
    const payload = (await response.json()) as ApiEnvelope<T> | T;

    if (payload && typeof payload === 'object' && 'data' in (payload as ApiEnvelope<T>)) {
      return (payload as ApiEnvelope<T>).data as T;
    }

    return payload as T;
  }

  getRecipes = async (search: string, status: string, category: string, page: number, pageSize: number): Promise<RecipeV2ListResponse> => {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (status) params.append('status', status);
    if (category) params.append('category', category);
    params.append('page', String(page));
    params.append('pageSize', String(pageSize));

    const response = await fetch(`${this.baseUrl}?${params.toString()}`, { headers });
    const paged = await this.readResponse<PagedRecipeApiDto>(response, 'Failed to fetch recipes.');

    return {
      data: paged.data.map((recipe) => mapRecipe(recipe)),
      totalCount: paged.totalCount,
      page: paged.page,
      pageSize: paged.pageSize,
    };
  };

  getRecipeBankList = async (
    search: string,
    category: string,
    dietaryAttributeIds: number[],
    page: number,
    pageSize: number,
  ): Promise<RecipeV2ListResponse> => {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (category) params.append('category', category);
    dietaryAttributeIds.forEach((id) => params.append('dietaryAttributeIds', String(id)));
    params.append('page', String(page));
    params.append('pageSize', String(pageSize));

    const response = await fetch(`${this.baseUrl}/bank?${params.toString()}`, { headers });
    const paged = await this.readResponse<PagedRecipeApiDto>(
      response,
      'Failed to fetch the recipe bank.',
    );
    return {
      data: paged.data.map((dto) => mapRecipe(dto, true)),
      totalCount: paged.totalCount,
      page: paged.page,
      pageSize: paged.pageSize,
    };
  };

  async getRecipeBankDetail(id: number): Promise<RecipeV2Detail> {
    const response = await fetch(`${this.baseUrl}/bank/${id}`, {
      headers: await this.getHeaders(),
    });
    const dto = await this.readResponse<RecipeApiDto>(
      response,
      'Failed to fetch the published recipe.',
    );
    const detail = mapRecipe(dto, true) as RecipeV2Detail;
    detail.steps = (dto.steps ?? []).map(mapStep);
    return detail;
  }

  async exportRecipeDocument(id: number, informationProfile: RecipeExportInformationProfile, presentationStyle: RecipeExportPresentationStyle): Promise<Blob> {
    const params = new URLSearchParams({ informationProfile, presentationStyle });
    const response = await fetch(`${this.baseUrl}/bank/${id}/export-document?${params}`, {
      headers: await this.getHeaders(),
    });
    if (!response.ok) throw new Error(await this.extractErrorMessage(response, 'Failed to export recipe document.'));
    return response.blob();
  }

  async getRecipePublishingDetail(id: number): Promise<RecipeV2Detail> {
    const response = await fetch(`${this.baseUrl}/bank/${id}/publish`, {
      headers: await this.getHeaders(),
    });
    const dto = await this.readResponse<RecipePublishingApiDto>(
      response,
      'Failed to fetch the governed recipe for publishing.',
    );
    const detail = mapPublishingRecipe(dto) as RecipeV2Detail;
    detail.steps = (dto.steps ?? []).map(mapStep);
    return detail;
  }

  async getRecipeAudienceDetail(id: number): Promise<RecipeAudienceDetail> {
    const response = await fetch(`${this.baseUrl}/bank/${id}/audience`, {
      headers: await this.getHeaders(),
    });
    const dto = await this.readResponse<RecipeAudienceApiDto>(
      response,
      'Failed to fetch the audience-safe recipe.',
    );
    return mapAudienceRecipe(dto);
  }

  async getRecipeDetail(id: number): Promise<RecipeV2Detail> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${id}`, { headers });
    const recipe = await this.readResponse<RecipeApiDto>(response, 'Failed to fetch recipe.');
    const detail = mapRecipe(recipe) as RecipeV2Detail;
    detail.steps = await this.getSteps(id);
    return detail;
  }

  async getVersionHistory(id: number): Promise<RecipeVersionHistory[]> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${id}/versions`, { headers });
    const versions = await this.readResponse<RecipeVersionHistoryApiDto[]>(
      response,
      'Failed to fetch recipe version history.',
    );
    return versions.map(mapVersionHistory);
  }

  async getVersionDetail(id: number, versionNumber: number): Promise<RecipeVersionDetail> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${id}/versions/${versionNumber}`, { headers });
    const detail = await this.readResponse<RecipeVersionDetailApiDto>(
      response,
      `Failed to fetch recipe version ${versionNumber}.`,
    );
    return mapVersionDetail(detail);
  }

  async createRecipe(data: CreateRecipeRequest): Promise<RecipeV2> {
    const headers = await this.getHeaders();
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    const recipe = await this.readResponse<RecipeApiDto>(response, 'Failed to create recipe.');
    return mapRecipe(recipe);
  }

  async updateRecipe(id: number, data: UpdateRecipeMetadataRequest): Promise<RecipeV2Detail> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });
    const recipe = await this.readResponse<RecipeApiDto>(response, 'Failed to update recipe.');
    return mapRecipe(recipe) as RecipeV2Detail;
  }

  // Publishes the recipe's current Draft RecipeVersion, ending the create/edit wizard.
  async finishRecipe(id: number): Promise<RecipeV2> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${id}/finish`, {
      method: 'POST',
      headers,
    });
    if (!response.ok) {
      await this.readResponse<unknown>(response, 'Failed to finish recipe.');
    }
    return this.getRecipeDetail(id);
  }

  // Real franchise-scoped reference data for the create form's Production Method dropdown.
  getProductionMethods = async (franchiseId?: number): Promise<ProductionMethod[]> => {
    const headers = await this.getHeaders();
    const suffix = franchiseId ? `?franchiseId=${franchiseId}` : '';
    const response = await fetch(`${this.baseUrl}/production-methods${suffix}`, { headers });
    return this.readResponse<ProductionMethod[]>(response, 'Failed to fetch production methods.');
  };

  // Not yet implemented server-side.
  async deleteRecipe(id: number): Promise<RecipeV2> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${id}`, { method: 'DELETE', headers });
    const archived = await this.readResponse<RecipeApiDto>(response, 'Failed to archive recipe.');
    return mapRecipe(archived);
  }

  async getRecipeUsage(id: number): Promise<RecipeMenuUsage[]> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${id}/usage`, { headers });
    return this.readResponse<RecipeMenuUsage[]>(response, 'Failed to fetch menu usage.');
  }

  async cloneRecipe(id: number, data: CloneRecipeRequest): Promise<RecipeV2> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${id}/clone`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok && response.status >= 500) {
      throw new Error('Failed to clone recipe. Please try again.');
    }

    const recipe = await this.readResponse<RecipeApiDto>(response, 'Failed to clone recipe.');
    return mapRecipe(recipe);
  }

  async submitRecipe(id: number, reason?: string): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${id}/submit`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ reason }),
    });
    await this.readResponse<unknown>(response, 'Failed to submit recipe version.');
  }

  async approveRecipe(id: number, data: ApproveRejectRequest): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${id}/approve`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ reason: data.comment }),
    });
    await this.readResponse<unknown>(response, 'Failed to approve recipe version.');
  }

  async rejectRecipe(id: number, data: ApproveRejectRequest): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${id}/reject`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ reason: data.comment }),
    });
    await this.readResponse<unknown>(response, 'Failed to reject recipe version.');
  }

  async rollbackVersion(id: number, versionNumber: number, reason?: string): Promise<number> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${id}/versions/${versionNumber}/rollback`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ reason }),
    });
    return this.readResponse<number>(response, 'Failed to rollback recipe version.');
  }

  async addComponent(
    recipeId: number,
    component: Omit<UpdateComponentRequest, 'sortOrder'> & { componentProductId: number; componentItemNumber: string; componentDescription: string },
  ): Promise<RecipeComponent> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${recipeId}/components`, {
      method: 'POST', headers,
      body: JSON.stringify({
        componentItemId: component.componentProductId,
        quantity: component.quantity,
        uom: component.uom,
        weight: component.weight,
        volume: component.volume,
        componentRole: component.componentRole ?? 'Ingredient',
      }),
    });
    await this.readResponse<unknown>(response, 'Failed to add component.');
    const recipe = await this.getRecipeDetail(recipeId);
    const saved = (recipe.components ?? []).find((c) => c.componentProductId === component.componentProductId);
    if (!saved) throw new Error('Added component could not be loaded.');
    return saved;
  }

  async updateComponent(recipeId: number, componentId: number, data: UpdateComponentRequest): Promise<RecipeComponent> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${recipeId}/components/${componentId}`, {
      method: 'PUT', headers,
      body: JSON.stringify({
        componentItemId: data.componentProductId, quantity: data.quantity,
        uom: data.uom, weight: data.weight, volume: data.volume,
        componentRole: data.componentRole ?? 'Ingredient',
      }),
    });
    await this.readResponse<unknown>(response, 'Failed to update component.');
    const recipe = await this.getRecipeDetail(recipeId);
    const saved = (recipe.components ?? []).find((c) =>
      data.componentKey
        ? c.componentKey === data.componentKey
        : c.id === componentId);
    if (!saved) throw new Error('Updated component could not be loaded.');
    return saved;
  }

  async removeComponent(recipeId: number, componentId: number): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${recipeId}/components/${componentId}`, { method: 'DELETE', headers });
    await this.readResponse<unknown>(response, 'Failed to remove component.');
  }

  async reorderComponents(recipeId: number, componentIds: number[]): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${recipeId}/components/order`, {
      method: 'PUT', headers, body: JSON.stringify({ componentIds }),
    });
    await this.readResponse<unknown>(response, 'Failed to reorder components.');
  }

  async convertToStock(recipeId: number, request: ConvertToStockRequest): Promise<ConvertToStockResult> {
    const headers = await this.getHeaders();
    const idempotencyKey = request.idempotencyKey
      ?? globalThis.crypto?.randomUUID?.()
      ?? `recipe-production-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

    const response = await fetch(`${this.baseUrl}/${recipeId}/production`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        quantity: request.quantity,
        uom: request.uom,
        idempotencyKey,
      }),
    });

    const result = await this.readResponse<{
      transactionId: number;
      eventReference: string;
      recipeId: number;
      recipeVersionId: number;
      recipePublicationId: number | null;
      outputQuantity: number;
      outputUom: string;
      recipeExecutions: number;
      storageMethod: string;
      shelfLifeValue: number;
      shelfLifeUnit: string;
      expectedYieldLossPercent: number | null;
    }>(response, 'Failed to produce recipe stock.');

    return {
      transactionId: result.transactionId,
      eventReference: result.eventReference,
      recipeId: result.recipeId,
      recipeVersionId: result.recipeVersionId,
      recipePublicationId: result.recipePublicationId,
      quantity: result.outputQuantity,
      uom: result.outputUom,
      recipeExecutions: result.recipeExecutions,
      storageMethod: result.storageMethod,
      shelfLifeValue: result.shelfLifeValue,
      shelfLifeUnit: result.shelfLifeUnit,
      expectedYieldLossPercent: result.expectedYieldLossPercent,
    };
  }

  async getSteps(recipeId: number): Promise<RecipeStep[]> {
    const response = await fetch(`${this.baseUrl}/${recipeId}/steps`, { headers: await this.getHeaders() });
    return (await this.readResponse<RecipeStepApiDto[]>(response, 'Failed to fetch recipe steps.')).map(mapStep);
  }

  async addStep(recipeId: number, step: CreateUpdateStepRequest): Promise<RecipeStep> {
    const response = await fetch(`${this.baseUrl}/${recipeId}/steps`, { method: 'POST', headers: await this.getHeaders(), body: JSON.stringify(step) });
    return mapStep(await this.readResponse<RecipeStepApiDto>(response, 'Failed to add recipe step.'));
  }

  async updateStep(recipeId: number, stepId: number, step: CreateUpdateStepRequest): Promise<RecipeStep> {
    const response = await fetch(`${this.baseUrl}/${recipeId}/steps/${stepId}`, { method: 'PUT', headers: await this.getHeaders(), body: JSON.stringify(step) });
    return mapStep(await this.readResponse<RecipeStepApiDto>(response, 'Failed to update recipe step.'));
  }

  async removeStep(recipeId: number, stepId: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${recipeId}/steps/${stepId}`, { method: 'DELETE', headers: await this.getHeaders() });
    await this.readResponse<unknown>(response, 'Failed to remove recipe step.');
  }

  async reorderSteps(recipeId: number, stepIds: number[]): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${recipeId}/steps/order`, { method: 'PUT', headers: await this.getHeaders(), body: JSON.stringify({ stepIds }) });
    await this.readResponse<unknown>(response, 'Failed to reorder recipe steps.');
  }

  async getRecipeMediaUrl(recipeId: number, stepId?: number): Promise<string | null> {
    const params = stepId ? `?stepId=${stepId}` : '';
    const response = await fetch(`${this.baseUrl}/${recipeId}/media${params}`, { headers: await this.getHeaders() });
    const result = await this.readResponse<{ url?: string | null }>(response, 'Failed to fetch recipe image.');
    return result.url ?? null;
  }

  async authorizeRecipeMediaUpload(recipeId: number, file: File, stepId?: number): Promise<RecipeMediaUploadAuthorization> {
    const response = await fetch(`${this.baseUrl}/${recipeId}/media/uploads`, {
      method: 'POST', headers: await this.getHeaders(),
      body: JSON.stringify({ stepId, contentType: file.type, contentLength: file.size }),
    });
    return this.readResponse<RecipeMediaUploadAuthorization>(response, 'Failed to authorize recipe image upload.');
  }

  async finalizeRecipeMediaUpload(recipeId: number, uploadId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${recipeId}/media/uploads/finalize`, { method: 'POST', headers: await this.getHeaders(), body: JSON.stringify({ uploadId }) });
    await this.readResponse<unknown>(response, 'Failed to finalize recipe image upload.');
  }

  async removeRecipeMedia(recipeId: number, stepId?: number): Promise<void> {
    const params = stepId ? `?stepId=${stepId}` : '';
    const response = await fetch(`${this.baseUrl}/${recipeId}/media${params}`, { method: 'DELETE', headers: await this.getHeaders() });
    await this.readResponse<unknown>(response, 'Failed to remove recipe image.');
  }

  getAllergens(franchiseId?: number): Promise<Allergen[]> {
    return this.getHeaders().then(async headers => {
      const suffix = franchiseId ? `?franchiseId=${franchiseId}` : '';
      const response = await fetch(`${this.baseUrl}/allergens${suffix}`, { headers });
      const allergens = await this.readResponse<Array<{ allergenId: number; code: string; name: string }>>(
        response, 'Failed to fetch allergens.');
      return allergens.map(allergen => ({ id: allergen.allergenId, code: allergen.code, name: allergen.name, iconUrl: '' }));
    });
  }

  async overrideAllergen(recipeId: number, override: AllergenOverride): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${recipeId}/allergens`, {
      method: 'POST', headers,
      body: JSON.stringify({ allergenId: override.allergenId, status: override.overrideStatus, notes: override.reason }),
    });
    await this.readResponse<unknown>(response, 'Failed to save manual allergen.');
  }

  async clearAllergenOverride(recipeId: number, allergenId: number, reason: string): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${recipeId}/allergens/${allergenId}`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ reason }),
    });
    if (response.status === 204) return;
    await this.readResponse<unknown>(response, 'Failed to remove manual allergen.');
  }

  async getDietaryAttributes(recipeId: number): Promise<DietaryAttribute[]> {
    return (await this.getRecipeDetail(recipeId)).dietaryAttributes ?? [];
  }

  getDietaryAttributeReference(franchiseId?: number): Promise<DietaryAttributeReference[]> {
    return this.getHeaders().then(async headers => {
      const suffix = franchiseId ? `?franchiseId=${franchiseId}` : '';
      const response = await fetch(`${this.baseUrl}/dietary-attributes${suffix}`, { headers });
      const attributes = await this.readResponse<Array<{ dietaryAttributeId: number; code: string; name: string; category?: string | null }>>(
        response, 'Failed to fetch dietary attributes.');
      return attributes.map(attribute => ({ id: attribute.dietaryAttributeId, code: attribute.code, name: attribute.name, category: toDietaryCategory(attribute.category) }));
    });
  }

  async overrideDietaryAttribute(recipeId: number, override: DietaryAttributeOverride): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${recipeId}/dietary-attributes`, {
      method: 'POST', headers,
      body: JSON.stringify({ dietaryAttributeId: override.attributeId, status: override.status, notes: override.reason }),
    });
    await this.readResponse<unknown>(response, 'Failed to save dietary attribute override.');
  }

  async clearDietaryAttributeOverride(recipeId: number, attributeId: number, reason: string): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${recipeId}/dietary-attributes/${attributeId}`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ reason }),
    });
    if (response.status === 204) return;
    await this.readResponse<unknown>(response, 'Failed to remove dietary attribute override.');
  }

  getNutrients(franchiseId?: number): Promise<NutrientReference[]> {
    return this.getHeaders().then(async headers => {
      const suffix = franchiseId ? `?franchiseId=${franchiseId}` : '';
      const response = await fetch(`${this.baseUrl}/nutrients${suffix}`, { headers });
      const nutrients = await this.readResponse<Array<{ nutrientId: number; code: string; name: string }>>(
        response, 'Failed to fetch nutrients.');
      return nutrients.map(nutrient => ({ nutrientId: nutrient.nutrientId, code: nutrient.code, name: nutrient.name }));
    });
  }

  async overrideNutrition(recipeId: number, override: NutritionOverride): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${recipeId}/nutrition`, {
      method: 'POST', headers,
      body: JSON.stringify({ nutrientCode: override.nutrientCode, value: override.value, valueUom: override.valueUom, notes: override.notes }),
    });
    await this.readResponse<unknown>(response, 'Failed to save nutrition override.');
  }

  async clearNutritionOverride(recipeId: number, nutrientCode: string, reason: string): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${recipeId}/nutrition/${encodeURIComponent(nutrientCode)}`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ reason }),
    });
    if (response.status === 204) return;
    await this.readResponse<unknown>(response, 'Failed to remove nutrition override.');
  }

  dismissAIFinding(recipeId: number, findingId: number, reason: string): Promise<void> {
    return this.fallback.dismissAIFinding(recipeId, findingId, reason);
  }

  acknowledgeAIFinding(recipeId: number, findingId: number): Promise<void> {
    return this.fallback.acknowledgeAIFinding(recipeId, findingId);
  }

  async simulateCost(id: number, componentId: number, priceChangePercent: number): Promise<CostSimulationResult> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${id}/cost-simulation`, {
      method: 'POST', headers, body: JSON.stringify({ componentId, priceChangePercent }),
    });
    return this.readResponse<CostSimulationResult>(response, 'Failed to simulate cost.');
  }

  getSupplierComparison(id: number): Promise<ComponentSupplierComparison[]> {
    return this.fallback.getSupplierComparison(id);
  }

  async searchStockItems(search: string): Promise<StockItemSearchResult[]> {
    const headers = await this.getHeaders();
    const storedStore = localStorage.getItem('selectedStore');
    const store = storedStore ? JSON.parse(storedStore) : null;
    if (!store?.storeNumber) throw new Error('Select a store before searching components.');
    const params = new URLSearchParams({ search, storeNumber: store.storeNumber, take: '25' });
    const response = await fetch(`/api/Items?${params}`, { headers });
    const result = await this.readResponse<{ items: ItemSearchApiDto[] }>(response, 'Failed to search items.');
    return (result.items ?? [])
      .filter((item) => item.isStockItem || item.isBom)
      .map((item) => ({ id: item.id, itemNumber: item.itemNumber, description: item.description,
        uom: item.uom, costPrice: item.costPrice, weightNet: item.weightNet, isRecipe: item.isBom }));
  }

  async exportRecipes(search: string, status: string, category: string, format: 'csv' | 'xlsx'): Promise<Blob> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (status) params.append('status', status);
    if (category) params.append('category', category);
    params.append('format', format);

    const response = await fetch(`${this.baseUrl}/export?${params.toString()}`, { headers });
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to export recipes.'));
    }
    return response.blob();
  }

  async exportBulkWorkbook(franchiseId: number): Promise<Blob> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams({ franchiseId: String(franchiseId) });
    const response = await fetch(`/api/recipev2/import-runs/workbook?${params.toString()}`, { headers });
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, 'Failed to export the Recipes V2 bulk workbook.'));
    }
    return response.blob();
  }

  async validateBulkWorkbook(franchiseId: number, idempotencyKey: string, file: File): Promise<RecipeBulkImportRun> {
    const headers = await this.getMultipartHeaders();
    const body = new FormData();
    body.append('franchiseId', String(franchiseId));
    body.append('idempotencyKey', idempotencyKey);
    body.append('file', file);
    const response = await fetch('/api/recipev2/import-runs/validate', {
      method: 'POST',
      headers,
      body,
    });
    return this.readResponse<RecipeBulkImportRun>(response, 'Failed to validate the Recipes V2 bulk workbook.');
  }

  async commitBulkWorkbook(runId: number, franchiseId: number, file: File): Promise<RecipeBulkImportRun> {
    const headers = await this.getMultipartHeaders();
    const body = new FormData();
    body.append('franchiseId', String(franchiseId));
    body.append('file', file);
    const response = await fetch(`/api/recipev2/import-runs/${runId}/commit`, {
      method: 'POST',
      headers,
      body,
    });
    return this.readResponse<RecipeBulkImportRun>(response, 'Failed to commit the Recipes V2 bulk workbook.');
  }

  async getBulkImportRun(runId: number): Promise<RecipeBulkImportRun> {
    const headers = await this.getHeaders();
    const response = await fetch(`/api/recipev2/import-runs/${runId}`, { headers });
    return this.readResponse<RecipeBulkImportRun>(response, 'Failed to fetch the Recipes V2 bulk import run.');
  }
}
